import { useMemo, useState } from 'react';
import ChartCard from '../charts/ChartCard';
import LineChart from '../charts/LineChart';
import { EMPTY, fmtAbsolute, fmtAgeShort, fmtInt, fmtNum } from '../../utils/statsFormat';
import './statistics.css';

/**
 * Section B — the receiver's live health.
 *
 * Absorbs PerfCharts wholesale, with four deliberate changes:
 *
 *  1. "Messages/sec" is split. It used to plot a rate (msg/s) and a cumulative
 *     counter on one shared y-scale, and the counter's magnitude squashed the
 *     rate flat. Two units, two charts.
 *  2. "System" is split into CPU/memory (%) and temperature (°C) for the same
 *     reason — they only looked compatible because both happen to sit in 0-100.
 *  3. Charts appear only when their column exists. Pass-through / Beast-in
 *     feeders never write cpuPct/memPct/tempC, and StatsReporter never emits
 *     msg_dropped at all, so those cards would otherwise sit permanently empty.
 *  4. Gauges show min/avg/max for the bucket via the band the backend now
 *     returns, instead of one averaged point that hides the spread.
 */

// STATS heartbeat is a 60s cadence; allow a grace window before going red.
const STATS_FRESH_S = 90;
const CFG_FRESH_S = 90;

/** A metric is present only if it actually has a non-null sample. */
function hasData(series, key) {
  const s = series?.[key];
  return Array.isArray(s) && s.some(p => p && p.v != null);
}

function samplesOf(series, key) {
  return (series?.[key] || []).filter(p => p && p.v != null);
}

function FreshnessAge({ label, ageS, freshWindowS }) {
  const known = ageS != null;
  const fresh = known && ageS <= freshWindowS;
  const cls = !known ? '' : fresh ? ' is-fresh' : ' is-stale';
  return (
    <div className={`stats-fresh-cell${cls}`}>
      <div className="stats-fresh-label">{label}</div>
      <div className="stats-fresh-value">
        {known && <span className="stats-fresh-mark">{fresh ? '✓' : '✕'}</span>}
        {known ? fmtAgeShort(ageS) : EMPTY}
      </div>
    </div>
  );
}

function FreshnessStamp({ label, ms }) {
  return (
    <div className="stats-fresh-cell">
      <div className="stats-fresh-label">{label}</div>
      <div className="stats-fresh-value">{ms == null ? EMPTY : fmtAbsolute(ms)}</div>
    </div>
  );
}

function LiveStat({ label, value }) {
  return (
    <div className="stats-live-stat">
      <div className="stats-live-value">{value == null ? EMPTY : value}</div>
      <div className="stats-live-label">{label}</div>
    </div>
  );
}

function ConnectionEvents({ events = [] }) {
  const [open, setOpen] = useState(false);
  if (events.length === 0) return null;

  return (
    <div className="stats-events">
      <button type="button" className="stats-events-toggle" onClick={() => setOpen(o => !o)}>
        <span>{open ? '▾' : '▸'}</span>
        Connection events
        <span className="stats-events-count">{events.length}</span>
      </button>
      {open && (
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr><th>When</th><th>Event</th><th>Source</th><th>Reason</th><th>Message</th></tr>
            </thead>
            <tbody>
              {events.map((ev, i) => (
                <tr key={`${ev.connectionId || 'x'}-${ev.timestamp || i}-${i}`}>
                  <td className="stats-td-nowrap">{eventTime(ev.timestamp)}</td>
                  <td className={eventClass(ev.eventType)}>{ev.eventType}</td>
                  <td className="stats-td-mono">
                    {ev.sourceIp}{ev.sourcePort ? `:${ev.sourcePort}` : ''}
                  </td>
                  <td>{ev.reason || ''}</td>
                  <td className="stats-td-mono">{ev.message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Backend serialises LocalDateTime without a zone; those are UTC. */
function eventTime(s) {
  if (!s) return EMPTY;
  const iso = /Z|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function eventClass(t) {
  if (t === 'DISCONNECTED' || t === 'UUID_INVALID' || t === 'RECEIVER_REAPED') return 'stats-ev-bad';
  if (t === 'RECEIVER_STALE' || t === 'UNKNOWN_RECEIVER_DETECTED') return 'stats-ev-warn';
  if (t === 'UUID_RECEIVED' || t === 'FIRST_BEAST_MESSAGE') return 'stats-ev-good';
  return '';
}

function LiveHealthSection({
  series = {},
  latest = null,
  events = [],
  range,
  loading = false,
}) {
  const fresh = useMemo(() => {
    const now = Date.now();
    const statsTs = latest?.ts ?? null;
    const uptime = latest?.values?.uptime_s;
    return {
      statsAge: statsTs ? Math.max(0, Math.floor((now - statsTs) / 1000)) : null,
      cfgAge: latest?.configHeartbeatAt
        ? Math.max(0, Math.floor((now - new Date(latest.configHeartbeatAt).getTime()) / 1000))
        : null,
      restartMs: (statsTs && Number.isFinite(Number(uptime)))
        ? statsTs - Number(uptime) * 1000 : null,
      configMs: latest?.configUpdatedAt ? new Date(latest.configUpdatedAt).getTime() : null,
    };
  }, [latest]);

  const v = (k) => latest?.values?.[k];
  const bucketNote = range ? `${Math.round(range.stepSeconds / 60)} min buckets` : null;

  return (
    <section className="stats-section" id="live">
      <header className="stats-section-header">
        <h2 className="stats-section-heading">Live health</h2>
        <span className="stats-section-sub">{range?.label}</span>
      </header>

      {latest && latest.live === false && (
        <div className="stats-lastknown-banner" role="status">
          <strong>Not reporting right now — these are its last known values.</strong>{' '}
          Nothing below is live; "Last STATS" shows how old it is.
        </div>
      )}

      <div className="stats-fresh-panel">
        <FreshnessAge label="Last STATS" ageS={fresh.statsAge} freshWindowS={STATS_FRESH_S} />
        <FreshnessAge label="Last CFG-VER" ageS={fresh.cfgAge} freshWindowS={CFG_FRESH_S} />
        <FreshnessStamp label="Last restart" ms={fresh.restartMs} />
        <FreshnessStamp label="Last config update" ms={fresh.configMs} />
      </div>

      <div className="stats-live-strip">
        <LiveStat label="msg/s" value={fmtNum(v('msg_rx_rate'), 1)} />
        <LiveStat label="aircraft" value={fmtInt(v('aircraft_now'))} />
        <LiveStat label="range nm" value={fmtNum(v('max_range_nm'), 0)} />
        <LiveStat label="gain dB" value={fmtNum(v('current_gain_db'), 1)} />
        <LiveStat label="CPU %" value={fmtNum(v('cpu_pct'), 0)} />
        <LiveStat label="mem %" value={fmtNum(v('mem_pct'), 0)} />
        <LiveStat label="temp °C" value={fmtNum(v('temp_c'), 0)} />
      </div>

      <div className="chart-grid">
        {/* B3 — a rate, on its own scale. */}
        <ChartCard title="Message rate" subtitle="msg/s" note={bucketNote} loading={loading}
                   empty={!hasData(series, 'msg_rx_rate')}>
          <LineChart unit="msg/s"
                     series={[{ name: 'rx rate', slot: 1, samples: samplesOf(series, 'msg_rx_rate') }]} />
        </ChartCard>

        {/* B4 — only when the column exists at all (see D4). */}
        {hasData(series, 'msg_dropped') && (
          <ChartCard title="Messages dropped" subtitle="per second" note={bucketNote} loading={loading}>
            <LineChart unit="msg/s"
                       series={[{ name: 'dropped/s', slot: 6, samples: samplesOf(series, 'msg_dropped') }]} />
          </ChartCard>
        )}

        {/* B5 — all series are aircraft counts, so one scale is honest. "now" is ADS-B
            (DF17); Mode-S-only aircraft (DF11 with no DF17, receiver 1.05.26+) are drawn as a
            separate line, not folded in, so the ADS-B count stays comparable across versions. */}
        <ChartCard title="Aircraft tracked" subtitle="count" note={bucketNote} loading={loading}
                   empty={!hasData(series, 'aircraft_now') && !hasData(series, 'aircraft_peak_1m')}
                   legend={[{ name: 'now', slot: 1 }, { name: 'peak / min', slot: 2 },
                            ...(hasData(series, 'aircraft_modes_now') ? [{ name: 'Mode-S only', slot: 3 }] : [])]}>
          <LineChart format={fmtInt} series={[
            { name: 'now', slot: 1, samples: samplesOf(series, 'aircraft_now') },
            { name: 'peak / min', slot: 2, samples: samplesOf(series, 'aircraft_peak_1m') },
            { name: 'Mode-S only', slot: 3, samples: samplesOf(series, 'aircraft_modes_now') },
          ]} />
        </ChartCard>

        {/* B6 — the band is the point here: peak vs typical vs worst minute. */}
        <ChartCard title="Max range" subtitle="nm · band = min/max per bucket" note={bucketNote}
                   loading={loading} empty={!hasData(series, 'max_range_nm')}>
          <LineChart unit="nm" format={(x) => fmtNum(x, 0)}
                     series={[{ name: 'max range', slot: 1, samples: samplesOf(series, 'max_range_nm') }]} />
        </ChartCard>

        {/* B7 — all four are dBFS, so they share a scale legitimately. */}
        <ChartCard title="Signal levels" subtitle="dBFS" note={bucketNote} loading={loading}
                   empty={!hasData(series, 'rssi_avg_dbfs')}
                   legend={[{ name: 'min', slot: 2 }, { name: 'avg', slot: 1 },
                            { name: 'max', slot: 3 }, { name: 'noise floor', slot: 6 }]}>
          <LineChart unit="dBFS" series={[
            { name: 'min', slot: 2, samples: samplesOf(series, 'rssi_min_dbfs') },
            { name: 'avg', slot: 1, samples: samplesOf(series, 'rssi_avg_dbfs') },
            { name: 'max', slot: 3, samples: samplesOf(series, 'rssi_max_dbfs') },
            { name: 'noise floor', slot: 6, samples: samplesOf(series, 'noise_floor_dbfs') },
          ]} />
        </ChartCard>

        {/* B8 — percentages only. */}
        {(hasData(series, 'cpu_pct') || hasData(series, 'mem_pct')) && (
          <ChartCard title="CPU & memory" subtitle="%" note={bucketNote} loading={loading}
                     legend={[{ name: 'CPU', slot: 1 }, { name: 'memory', slot: 2 }]}>
            <LineChart unit="%" yMin={0} yMax={100} series={[
              { name: 'CPU', slot: 1, samples: samplesOf(series, 'cpu_pct') },
              { name: 'memory', slot: 2, samples: samplesOf(series, 'mem_pct') },
            ]} />
          </ChartCard>
        )}

        {/* B9 — degrees, kept off the percentage scale. */}
        {hasData(series, 'temp_c') && (
          <ChartCard title="Temperature" subtitle="°C" note={bucketNote} loading={loading}>
            <LineChart unit="°C" format={(x) => fmtNum(x, 1)}
                       series={[{ name: 'temp', slot: 4, samples: samplesOf(series, 'temp_c') }]} />
          </ChartCard>
        )}

        {/* B10 — Beast-in receivers have no tuner, so no gain to report. */}
        {hasData(series, 'current_gain_db') && (
          <ChartCard title="Gain" subtitle="dB" note={bucketNote} loading={loading}>
            <LineChart unit="dB"
                       series={[{ name: 'gain', slot: 1, samples: samplesOf(series, 'current_gain_db') }]} />
          </ChartCard>
        )}
      </div>

      <ConnectionEvents events={events} />
    </section>
  );
}

export default LiveHealthSection;
